/**
 * PWA install helpers (shared by InstallPrompt banner, AccountSettings and
 * the Landing "get the app" section).
 *
 * IMPORTANT: this module is imported for its side effect in src/index.js —
 * Chrome fires `beforeinstallprompt` very early (often before React mounts
 * the Dashboard), so the event MUST be captured at module scope or it's
 * lost and the install button silently does nothing.
 */
import { useEffect, useState } from "react";
import { isNativeApp } from "@/lib/platform";

// ---------- module-level beforeinstallprompt capture ----------
let deferredPrompt = null;
let installedFlag = false;
const listeners = new Set();

function notify() {
    listeners.forEach((fn) => fn());
}

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredPrompt = e;
        notify();
    });
    window.addEventListener("appinstalled", () => {
        installedFlag = true;
        deferredPrompt = null;
        notify();
    });
}

// ---------- platform detection ----------
export function isStandalone() {
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator?.standalone === true
    );
}

export function isIOSBrowser() {
    const ua = navigator.userAgent || "";
    // iPadOS 13+ masquerades as Mac — catch it via touch points
    const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    return (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) || iPadOS;
}

export function isIOSSafari() {
    if (!isIOSBrowser()) return false;
    const ua = navigator.userAgent;
    return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export function isAndroidBrowser() {
    return /Android/i.test(navigator.userAgent || "");
}

export function isMobileBrowser() {
    return isIOSBrowser() || isAndroidBrowser();
}

// ---------- dismissal persistence (req: reappear after a session or two) ----------
const DISMISS_KEY = "bm_install_dismiss_v2";
const SESSION_COUNT_KEY = "bm_install_sessions";
const SESSION_MARK_KEY = "bm_install_session_marked";
const RESHOW_AFTER_SESSIONS = 2;
const RESHOW_AFTER_DAYS = 7;

function sessionCount() {
    try {
        return parseInt(localStorage.getItem(SESSION_COUNT_KEY) || "0", 10) || 0;
    } catch {
        return 0;
    }
}

// Count one "session" per browser tab-session (side effect on import)
try {
    if (typeof window !== "undefined" && !sessionStorage.getItem(SESSION_MARK_KEY)) {
        sessionStorage.setItem(SESSION_MARK_KEY, "1");
        localStorage.setItem(SESSION_COUNT_KEY, String(sessionCount() + 1));
    }
} catch {
    // storage unavailable (private mode) — banner just shows every session
}

export function installDismissedRecently() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        const { ts, sess } = JSON.parse(raw);
        const days = (Date.now() - (ts || 0)) / (1000 * 60 * 60 * 24);
        if (days >= RESHOW_AFTER_DAYS) return false;
        return sessionCount() - (sess || 0) < RESHOW_AFTER_SESSIONS;
    } catch {
        return false;
    }
}

export function recordInstallDismiss() {
    try {
        localStorage.setItem(DISMISS_KEY, JSON.stringify({ ts: Date.now(), sess: sessionCount() }));
    } catch {
        // ignore
    }
}

/**
 * Hook powering every install surface.
 *   available       — user could install (mobile/desktop web, not installed, not native app)
 *   platform        — "ios" | "android" | "desktop"
 *   hasNativePrompt — Chrome's beforeinstallprompt is armed (one-tap install)
 *   promptInstall() — resolves to "accepted" | "dismissed" | "show-ios-help" | "show-android-help"
 */
export function usePwaInstall() {
    const [, force] = useState(0);

    useEffect(() => {
        const fn = () => force((n) => n + 1);
        listeners.add(fn);
        return () => listeners.delete(fn);
    }, []);

    const platform = isIOSBrowser() ? "ios" : isAndroidBrowser() ? "android" : "desktop";
    const available = !isNativeApp() && !isStandalone() && !installedFlag;

    const promptInstall = async () => {
        if (deferredPrompt) {
            const p = deferredPrompt;
            deferredPrompt = null;
            p.prompt();
            const { outcome } = await p.userChoice;
            if (outcome === "accepted") {
                installedFlag = true;
                notify();
                return "accepted";
            }
            return "dismissed";
        }
        if (platform === "ios") return "show-ios-help";
        return "show-android-help";
    };

    return {
        available,
        installed: installedFlag,
        platform,
        isMobile: platform !== "desktop",
        hasNativePrompt: Boolean(deferredPrompt),
        promptInstall,
    };
}
